import { VcuFileItem } from './response/vcu-files-response';

/** Nó da árvore do explorador (pasta ou arquivo). */
export interface VcuExplorerNode {
  /** Nome exibido (segmento do caminho ou nome do arquivo). */
  name: string;
  /** Caminho completo lógico (ex.: C:/FuelTech Vision/Maps). */
  fullPath: string;
  isFolder: boolean;
  /** Filhos (pastas e arquivos); apenas para pastas. */
  children: VcuExplorerNode[];
  /** Dados do arquivo quando isFolder === false. */
  file?: VcuFileItem;
  /** Tipo para exibição: 'Pasta' | 'Mapa' | 'Log'. */
  typeLabel: 'folder' | 'map' | 'log';
  modifiedDate?: string | null;
  createdDate?: string | null;
  logDuration?: string | number | null;
}

/**
 * Detecta o tipo do arquivo pela extensão do nome.
 * .ftml/.ftlog → 'log'; .ftm/.ftmap → 'map'; demais → null (deixa o caller decidir).
 */
function typeForExtension(fileName: string | null | undefined): 'map' | 'log' | null {
  if (!fileName) return null;
  const dot = fileName.lastIndexOf('.');
  if (dot < 0) return null;
  const ext = fileName.slice(dot + 1).toLowerCase();
  if (ext === 'ftml' || ext === 'ftlog') return 'log';
  if (ext === 'ftm' || ext === 'ftmap') return 'map';
  return null;
}

/**
 * Normaliza um caminho lógico para usar sempre / e remove drive (C:) se existir
 * para manter consistência na árvore.
 */
export function normalizePath(path: string): string {
  if (!path || !path.trim()) return '';
  const normalized = path.replace(/\\/g, '/').trim();
  // Remove C: ou D: no início para ter uma raiz única
  const withoutDrive = normalized.replace(/^[a-zA-Z]:\/?/, '');
  return withoutDrive.startsWith('/') ? withoutDrive : '/' + withoutDrive;
}

/**
 * Monta a árvore de pastas/arquivos a partir de uma lista de itens.
 * Usa pathFile (caminho lógico do backend) para construir a hierarquia.
 * Arquivos sem pathFile são colocados na raiz com seu fileName.
 */
export function buildExplorerTree(
  mapItems: VcuFileItem[],
  logItems: VcuFileItem[],
  pathField: keyof VcuFileItem = 'pathFile'
): VcuExplorerNode {
  const root: VcuExplorerNode = {
    name: '/',
    fullPath: '/',
    isFolder: true,
    children: [],
    typeLabel: 'folder',
  };

  const pathToNode = new Map<string, VcuExplorerNode>();
  pathToNode.set('/', root);

  function ensureFolder(path: string): VcuExplorerNode {
    if (pathToNode.has(path)) return pathToNode.get(path)!;
    const segments = path.split('/').filter(Boolean);
    let currentPath = '';
    let parent = root;
    for (const segment of segments) {
      currentPath = currentPath ? `${currentPath}/${segment}` : `/${segment}`;
      let node = pathToNode.get(currentPath);
      if (!node) {
        node = {
          name: segment,
          fullPath: currentPath,
          isFolder: true,
          children: [],
          typeLabel: 'folder',
        };
        pathToNode.set(currentPath, node);
        parent.children.push(node);
      }
      parent = node;
    }
    return parent;
  }

  function addFile(item: VcuFileItem, type: 'map' | 'log') {
    const rawPath = (item as any)[pathField] as string | undefined;
    const fileName = item.fileName || item.id || 'arquivo';
    const extOverride = typeForExtension(item.fileName);
    const fileType = (
      extOverride
      ?? (item.type === 'map' || item.type === 'log' ? item.type : type)
    ) as 'map' | 'log';

    let logicalPath: string;
    let parent: VcuExplorerNode;
    let name: string;

    if (rawPath && rawPath.trim()) {
      logicalPath = normalizePath(rawPath);
      const segments = logicalPath.split('/').filter(Boolean);
      if (segments.length === 0) return;
      const dirPath = '/' + segments.slice(0, -1).join('/');
      parent = ensureFolder(dirPath);
      name = segments[segments.length - 1];
    } else {
      logicalPath = '/' + fileName;
      parent = root;
      name = fileName;
    }

    const fileNode: VcuExplorerNode = {
      name,
      fullPath: logicalPath,
      isFolder: false,
      children: [],
      file: item,
      typeLabel: fileType,
      modifiedDate: item.modifiedDate ?? null,
      createdDate: item.createdDate ?? null,
      logDuration: item.logDuration ?? null,
    };
    parent.children.push(fileNode);
  }

  mapItems.forEach((item) => addFile(item, 'map'));
  logItems.forEach((item) => addFile(item, 'log'));

  sortExplorerNode(root);
  return root;
}

/** Ordena pastas primeiro (alfabético), depois arquivos (alfabético). */
function sortExplorerNode(node: VcuExplorerNode): void {
  node.children.sort((a, b) => {
    if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
  node.children.forEach(sortExplorerNode);
}

/**
 * Retorna os filhos diretos de um nó no caminho dado (path = fullPath do nó).
 */
export function getChildrenAtPath(root: VcuExplorerNode, path: string): VcuExplorerNode[] {
  const normalized = path || '/';
  const node = findNodeByPath(root, normalized);
  return node ? [...node.children] : [];
}

function findNodeByPath(node: VcuExplorerNode, path: string): VcuExplorerNode | null {
  if (node.fullPath === path) return node;
  for (const child of node.children) {
    const found = findNodeByPath(child, path);
    if (found) return found;
  }
  return null;
}
